import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDisconnectStripe } from '../hooks/use-stripe-mutations';

export interface DisconnectStripeDialogProps {
  storeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DisconnectStripeDialog({
  storeId,
  open,
  onOpenChange,
}: DisconnectStripeDialogProps) {
  const { t } = useTranslation(['paymentMethods', 'common', 'errors']);
  const disconnect = useDisconnectStripe(storeId);
  const message = useErrorMessage();
  const requestId = isApiError(disconnect.error) ? disconnect.error.requestId : undefined;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) disconnect.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('paymentMethods:stripe.disconnect.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('paymentMethods:stripe.disconnect.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {disconnect.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(disconnect.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={disconnect.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={disconnect.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              disconnect.mutate(undefined, { onSuccess: () => onOpenChange(false) });
            }}
          >
            {disconnect.isPending
              ? t('paymentMethods:stripe.disconnect.submitting')
              : t('paymentMethods:stripe.disconnect.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
